import styled from "styled-components";
import { Box } from "@mui/material";

export const PageContainer = styled(Box)`
  padding: 24px;
  min-height: calc(100vh - 64px);

  @media (max-width: 767px) {
    padding: 8px;
  }
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const PageTitle = styled.h1`
  font-size: 30px;
  font-weight: 400;
  color: #5a5a5a;
  margin: 0;

  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

export const AddBundleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #e39435;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  height: 50px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #d18429;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

export const BundleList = styled.div`
  display: grid;
  gap: 16px;
`;

export const BundleCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

export const BundleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

export const BundleName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
`;

export const BundleActions = styled.div`
  display: flex;
  gap: 8px;
`;

export const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  
  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }
`;

export const TableActionButton = styled.button<{ $variant?: 'edit' | 'delete' }>`
  padding: 6px 12px;
  background-color: ${props => props.$variant === 'delete' ? '#e53e3e' : '#e39435'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s;
  min-width: 70px;
  text-align: center;
  
  &:hover {
    background-color: ${props => props.$variant === 'delete' ? '#c53030' : '#d18429'};
  }
  
  @media (max-width: 480px) {
    width: 100%;
    padding: 8px 12px;
    font-size: 14px;
  }
`;

export const BundleDetails = styled.div`
  color: #718096;
  font-size: 14px;
  line-height: 1.5;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #718096;
`;

export const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #4a5568;
  margin: 0 0 8px 0;
`;

export const EmptyStateDescription = styled.p`
  font-size: 14px;
  color: #718096;
  margin: 0;
`;

// Modal and Form Styles
export const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

export const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 24px;
`;

export const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #718096;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background: #f7fafc;
  }
`;

export const FormContainer = styled.div`
  padding: 0 24px 24px 24px;
`;

export const FormStep = styled.div<{ isActive: boolean }>`
  display: ${props => props.isActive ? 'block' : 'none'};
`;

export const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
  gap: 16px;
`;

export const Step = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  background: ${props => 
    props.isActive ? '#e39435' : 
    props.isCompleted ? '#76b041' : '#f7fafc'
  };
  color: ${props => 
    props.isActive || props.isCompleted ? 'white' : '#718096'
  };
  font-size: 14px;
  font-weight: 500;
`;

export const FormGroup = styled.div`
  margin-bottom: 24px;
`;

export const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
`;

export const FormInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  height: 50px;
  padding: 12px 16px;
  border: 1px solid ${props => props.hasError ? '#e53e3e' : '#e2e8f0'};
  border-radius: 6px;
  font-size: 14px;
  color: #2d3748;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${props => props.hasError ? '#e53e3e' : '#76b041'};
  }

  &::placeholder {
    color: #a0aec0;
  }
`;

export const FormSelect = styled.select<{ hasError?: boolean }>`
  width: 100%;
  height: 50px;
  padding: 12px 16px;
  border: 1px solid ${props => props.hasError ? '#e53e3e' : '#e2e8f0'};
  border-radius: 6px;
  font-size: 14px;
  color: #2d3748;
  outline: none;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${props => props.hasError ? '#e53e3e' : '#76b041'};
  }
`;

export const FormTextArea = styled.textarea<{ hasError?: boolean }>`
  width: 100%;
  min-height: 100px;
  padding: 12px 16px;
  border: 1px solid ${props => props.hasError ? '#e53e3e' : '#e2e8f0'};
  border-radius: 6px;
  font-size: 14px;
  color: #2d3748;
  outline: none;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${props => props.hasError ? '#e53e3e' : '#76b041'};
  }

  &::placeholder {
    color: #a0aec0;
  }
`;

export const ErrorMessage = styled.div`
  color: #e53e3e;
  font-size: 12px;
  margin-top: 4px;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

export const ItemSelector = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 16px;
`;

export const ItemSelectorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const ItemSelectorTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
`;

export const RemoveItemButton = styled.button`
  background: none;
  border: none;
  color: #e53e3e;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background: #fed7d7;
  }
`;

export const AddItemButton = styled.button`
  width: 100%;
  height: 50px;
  border: 2px dashed #e2e8f0;
  background: none;
  border-radius: 6px;
  color: #718096;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #e39435;
    color: #e39435;
    background: #fef5e7;
  }
`;

export const FormActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
  margin-top: 32px;
`;

export const SecondaryButton = styled.button`
  background: none;
  border: 1px solid #e2e8f0;
  color: #718096;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #cbd5e0;
    background: #f7fafc;
  }
`;

export const PrimaryButton = styled.button<{ disabled?: boolean }>`
  background: ${props => props.disabled ? '#cbd5e0' : '#e39435'};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s;

  &:hover {
    background: ${props => props.disabled ? '#cbd5e0' : '#d18429'};
  }
`;

export const ReviewSection = styled.div`
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 24px;
`;

export const ReviewTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 16px 0;
`;

export const ReviewItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
`;

export const ReviewLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #4a5568;
  min-width: 120px;
`;

export const ReviewValue = styled.div`
  font-size: 14px;
  color: #2d3748;
  flex: 1;
  text-align: right;
`;

export const SuccessMessage = styled.div`
  background: #f0fff4;
  border: 1px solid #9ae6b4;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 24px;
  color: #276749;
  text-align: center;
`;

export const ErrorAlert = styled.div`
  background: #fed7d7;
  border: 1px solid #feb2b2;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 24px;
  color: #c53030;
  text-align: center;
`;
